import GlobalLayout from 'layout';
import Head from 'next/head';

import AuthorFeed from 'components/AuthorFeed';
import ContainedConversionPanel from 'components/ContainedConversionPanel';
import HeroAuthor from 'components/Hero/variants/Author';

import { getAuthorPath } from 'utils/authors';
import { fetchAuthorBySlug, fetchPostsByAuthor, resolveExternalContent } from 'utils/authors/fetchAuthorPage';
import { fetchLayoutModels } from 'utils/builder/builder.sdk';
import { getPersonSchema } from 'utils/schema/personSchema';

import type { Content } from '@builder.io/react';
import type { GetServerSideProps } from 'next';
import type { LayoutDataProps } from 'types/global';

// The design lays the deck out three-up, and the ticket asks for pagination past
// five rows of cards.
const POSTS_PER_PAGE = 15;

// Newsletter panel copy, verbatim from the mockup. Only the button comes from
// Builder (the `footers` global, already fetched for every page) so the
// subscribe destination can never drift from the one in the site footer.
const NEWSLETTER_HEADING = 'Stay in the loop';
const NEWSLETTER_BODY = 'Subscribe to our newsletter to get this content straight to your inbox.';

interface NewsletterContent {
  buttonLabel?: string;
  buttonLink?: string;
}

interface AuthorPageProps {
  author: AuthorReference;
  posts: { data: BlogResource }[];
  externalContent: { data: BlogResource }[];
  layoutData: LayoutDataProps;
}

const AuthorPage = ({ author, posts, externalContent, layoutData }: AuthorPageProps) => {
  const schemaJson = getPersonSchema(author);
  const newsletter = ((layoutData?.footerContent as Content | undefined)?.data?.newsletter || {}) as NewsletterContent;

  const seoData = {
    url: getAuthorPath(author),
    title: author.name,
    description: author.bio,
  };

  return (
    <>
      {schemaJson && (
        <Head>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJson) }} />
        </Head>
      )}
      <GlobalLayout seoData={seoData as unknown as Content} {...layoutData}>
        <HeroAuthor {...author} />
        <AuthorFeed id="author-posts" heading="Posts by this author" posts={posts} pageSize={POSTS_PER_PAGE} />
        <AuthorFeed id="author-further-reading" heading="Further reading" posts={externalContent} />
        <ContainedConversionPanel
          id="author-newsletter"
          heading={NEWSLETTER_HEADING}
          body={NEWSLETTER_BODY}
          button={{
            label: newsletter.buttonLabel || 'Subscribe',
            href: newsletter.buttonLink || '/subscribe/',
            hierarchy: 'primary',
          }}
          // Section only paints its light background when darkMode is set
          // explicitly; left undefined the panel sits on the dark page body.
          darkMode={false}
        />
      </GlobalLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, res, preview: draftPreview }) => {
  const slug = Array.isArray(params?.slug) ? params?.slug[0] : params?.slug || '';

  // A Builder outage must not 404 a live author page -- a 404 is a de-indexing
  // signal, a 500 is not -- so a failed person lookup is left to throw.
  const [author, layoutData] = await Promise.all([fetchAuthorBySlug(slug), fetchLayoutModels(false, !!draftPreview)]);

  // `person` holds every headshot the site uses, not just blog authors, so a
  // page only exists once someone opts that person in from Builder.
  if (!author?.data?.authorPageEnabled) {
    return { notFound: true };
  }

  const [posts, externalContent] = await Promise.all([
    // The decks are secondary content: if either lookup fails the profile is
    // still worth serving, so they degrade to empty rather than taking the page down.
    fetchPostsByAuthor(author.id).catch(error => {
      console.error(`Could not load posts for author ${slug}:`, error);

      return [];
    }),
    resolveExternalContent(author.data.externalContent),
  ]);

  // Same edge-cache posture as the blog post route: browsers never cache, the
  // Vercel edge serves a shared 5-minute snapshot and revalidates behind it.
  res.setHeader('Cache-Control', 'private, no-cache, must-revalidate');
  if (!draftPreview) {
    res.setHeader('CDN-Cache-Control', 'public, s-maxage=300, stale-while-revalidate=300');
  }

  return {
    props: { author: author.data, posts, externalContent, layoutData: layoutData || {} },
  };
};

export default AuthorPage;
